/**
 * ccBaseline.js
 * ─────────────────────────────────────────────────────────────
 * API layer for patient medical-asset baselines.
 *
 * POST /api/program/add-medical-assets
 * Payload (multipart/form-data):
 *   patient_id       string   (required)
 *   baseline_number  string   (required)  format: "ddmmyyyy001"
 *   ecg_link         string | null
 *   heart_sound_link string | null
 *   lung_sound_link  string | null
 *   bmi_link         string | null
 *   lab_report       File   | null
 * ─────────────────────────────────────────────────────────────
 */

const BASE = process.env.REACT_APP_API_BASE_URL;

function authHeaders(includeContentType = false) {
  const token = localStorage.getItem("token");
  const h = { Authorization: `Bearer ${token}` };
  if (includeContentType) h["Content-Type"] = "application/json";
  return h;
}

/* ─────────────────────────────────────────────────────────────
   Generate baseline number: ddmmyyyy + 3-digit sequence
   e.g. existingCount=0 → "04042026001"
        existingCount=1 → "04042026002"
   ─────────────────────────────────────────────────────────────
*/
export function generateBaselineNumber(existingCount = 0) {
  const now  = new Date();
  const dd   = String(now.getDate()).padStart(2, "0");
  const mm   = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = String(now.getFullYear());
  const seq  = String(existingCount + 1).padStart(3, "0");
  return `${dd}${mm}${yyyy}${seq}`;
}

/* ─────────────────────────────────────────────────────────────
   Format a raw baseline_number for display
   "04042026001" → "04-04-2026 · #001"
   Falls back gracefully for old BL-xxx format.
   ─────────────────────────────────────────────────────────────
*/
export function formatBaselineNumber(raw) {
  if (!raw) return "—";
  const s = String(raw);
  // new format: 11 chars, all digits
  if (/^\d{11}$/.test(s)) {
    const dd   = s.slice(0, 2);
    const mm   = s.slice(2, 4);
    const yyyy = s.slice(4, 8);
    const seq  = s.slice(8);
    return `${dd}-${mm}-${yyyy} · #${seq}`;
  }
  // legacy BL-xxx
  return s;
}

/* ─────────────────────────────────────────────────────────────
   Short label for strip pill: "04/04 #001"
   ─────────────────────────────────────────────────────────────
*/
export function shortBaselineLabel(raw) {
  if (!raw) return "—";
  const s = String(raw);
  if (/^\d{11}$/.test(s)) {
    const dd  = s.slice(0, 2);
    const mm  = s.slice(2, 4);
    const seq = s.slice(8);
    return `${dd}/${mm} #${seq}`;
  }
  return s;
}

/* ─────────────────────────────────────────────────────────────
   Add or update a medical-asset baseline.
   ─────────────────────────────────────────────────────────────
*/
export async function saveMedicalAssets({
  patientId,
  baselineNumber,
  ecgLink        = null,
  heartSoundLink = null,
  lungSoundLink  = null,
  bmiLink        = null,
  labReport      = null,
}) {
  const form = new FormData();
  form.append("patient_id",      patientId);
  form.append("baseline_number", baselineNumber);

  if (ecgLink)        form.append("ecg_link",         ecgLink);
  if (heartSoundLink) form.append("heart_sound_link",  heartSoundLink);
  if (lungSoundLink)  form.append("lung_sound_link",   lungSoundLink);
  if (bmiLink)        form.append("bmi_link",          bmiLink);
  if (labReport) {
    const safeName = labReport.name.replace(/\s+/g, "_");
    const safeFile = new File([labReport], safeName, { type: labReport.type });
    form.append("lab_report", safeFile);
  }

  const res = await fetch(`${BASE}/api/program/add-medical-assets`, {
    method:  "POST",
    headers: authHeaders(false),
    body:    form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Save medical assets failed: ${res.status}`);
  }

  return res.json();
}

/* ─────────────────────────────────────────────────────────────
   Construct a full lab-report URL from a stored key.
   ─────────────────────────────────────────────────────────────
*/
export function labReportUrl(key) {
  if (!key) return null;
  if (key.startsWith("http://") || key.startsWith("https://")) return key;
  const prefix = process.env.REACT_APP_LAB_REPORT_URL || "";
  return `${prefix}${key}`;
}